import axios, { AxiosInstance } from 'axios';
import { ATSAnalysis } from '../types';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

class ApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor to include auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Add response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Clear auth and redirect to login
          localStorage.removeItem('authToken');
          window.location.href = '/';
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth endpoints
  async getGoogleAuthUrl(): Promise<{ url: string; state: string }> {
    const response = await this.client.get('/auth/google');
    return response.data;
  }

  async handleGoogleCallback(code: string): Promise<{ token: string; user: any }> {
    const response = await this.client.get(`/auth/google/callback?code=${code}`);
    return response.data;
  }

  async getMe(): Promise<any> {
    const response = await this.client.get('/api/auth/me');
    return response.data;
  }

  // Journey endpoints
  async createJourney(): Promise<any> {
    const response = await this.client.post('/api/journeys');
    return response.data;
  }

  async getJourney(id: string): Promise<any> {
    const response = await this.client.get(`/api/journeys/${id}`);
    return response.data;
  }

  async listJourneys(): Promise<any> {
    const response = await this.client.get('/api/journeys');
    return response.data;
  }

  async sendMessage(journeyId: string, message: string): Promise<any> {
    const response = await this.client.post(`/api/journeys/${journeyId}/message`, {
      message,
    });
    return response.data;
  }

  async sendAudioMessage(journeyId: string, audioBlob: Blob): Promise<any> {
    const formData = new FormData();
    const fileExtension = audioBlob.type.includes('webm') ? 'webm' : 'mp4';
    formData.append('audio', audioBlob, `recording.${fileExtension}`);
    
    const response = await this.client.post(`/api/journeys/${journeyId}/message`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async moveToNextStage(journeyId: string): Promise<any> {
    const response = await this.client.post(`/api/journeys/${journeyId}/next-stage`);
    return response.data;
  }

  async updateJourneyStage(journeyId: string, stage: number): Promise<any> {
    const response = await this.client.put(`/api/journeys/${journeyId}/stage`, { stage });
    return response.data;
  }

  // Resume endpoints
  async getTemplates(): Promise<any> {
    const response = await this.client.get('/api/templates');
    return response.data;
  }

  async generateResume(
    journeyId: string,
    templateId: string,
    customization: any
  ): Promise<any> {
    const response = await this.client.post('/api/resumes/generate', {
      journeyId,
      templateId,
      customization,
    });
    return response.data;
  }

  async previewTemplate(journeyId: string, templateId: string): Promise<string> {
    const response = await this.client.post('/api/resumes/preview-template', {
      journeyId,
      templateId,
    }, {
      responseType: 'text',
    });
    return response.data;
  }

  async enhanceText(journeyId: string, text: string, context: string = 'job_description'): Promise<any> {
    const response = await this.client.post(`/api/journeys/${journeyId}/enhance`, {
      text,
      context,
    });
    return response.data;
  }

  async getDynamicFirstQuestion(journeyId: string): Promise<any> {
    const response = await this.client.get(`/api/journeys/${journeyId}/dynamic-question`);
    return response.data;
  }

  async updateJourneyCollectedData(journeyId: string, data: any): Promise<any> {
    const response = await this.client.put(`/api/journeys/${journeyId}/data`, { data });
    return response.data;
  }

  async getResume(id: string): Promise<any> {
    const response = await this.client.get(`/api/resumes/${id}`);
    return response.data;
  }

  async listResumes(): Promise<any> {
    const response = await this.client.get('/api/resumes');
    return response.data;
  }

  async downloadResume(id: string): Promise<Blob> {
    const response = await this.client.get(`/api/resumes/${id}/download`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async getResumePreview(id: string): Promise<string> {
    const response = await this.client.get(`/api/resumes/${id}/preview`, {
      responseType: 'text',
    });
    return response.data;
  }

  async updateResume(id: string, customization: any): Promise<any> {
    const response = await this.client.put(`/api/resumes/${id}`, {
      customization,
    });
    return response.data;
  }

  async updateResumeWithCustomization(id: string, payload: { data: any; customization: any }): Promise<any> {
    const response = await this.client.put(`/api/resumes/${id}`, payload);
    return response.data;
  }

  async previewResumeWithCustomization(resumeId: string, data: any, customization: any): Promise<string> {
    const response = await this.client.post('/api/resumes/preview-with-customization', {
      resumeId,
      data,
      customization,
    }, {
      responseType: 'text',
    });
    return response.data;
  }

  async checkATSScore(id: string): Promise<ATSAnalysis> {
    const response = await this.client.get(`/api/resumes/${id}/ats-check`);
    return response.data;
  }

  // Upload endpoints
  async uploadResume(file: File): Promise<any> {
    const formData = new FormData();
    formData.append('resume', file);

    const response = await this.client.post('/api/upload/resume', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async createJourneyWithParsedData(data: any): Promise<any> {
    const response = await this.client.post('/api/upload/create-journey', {
      data,
    });
    return response.data;
  }

  // Job Description Analyzer endpoints
  async analyzeJobDescription(resumeId: string, jobDescription: string): Promise<any> {
    const response = await this.client.post(`/api/resumes/${resumeId}/analyze-job`, {
      jobDescription,
    });
    return response.data;
  }

  async optimizeResumeForJob(resumeId: string, jobDescription: string): Promise<any> {
    const response = await this.client.post(`/api/resumes/${resumeId}/optimize-for-job`, {
      jobDescription,
    });
    return response.data;
  }
}

const api = new ApiService();

export default api;

